import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, Button, Form, Spinner, Nav } from "react-bootstrap";
import { Context } from "../App";
import AdminLayout from "../components/AdminLayout";
import AdminTheatreDetailsTable from "../components/AdminTheatreDetailsTable";
import {
  ADMIN_THEATRE_GALLERY_IMAGES,
  ADMIN_THEATRE_GALLERY_THEATERS,
  ADMIN_THEATRE_GALLERY_UPLOAD,
  ADMIN_THEATRE_GALLERY_SETTINGS,
  ADMIN_THEATRE_GALLERY_LEGACY,
  ADMIN_THEATRE_GALLERY_IMPORT_LEGACY,
  ADMIN_THEATRE_GALLERY_VIDEOS,
  ADMIN_THEATRE_GALLERY_UPLOAD_VIDEO,
  adminTheatreGallerySetActive,
  adminTheatreGallerySetVideoActive,
  adminTheatreGalleryDeleteImage,
  adminTheatreGalleryDeleteVideo,
  adminTheatreGalleryDownloadImage,
  adminTheatreGalleryDownloadVideo,
} from "../api/constants";
import { absoluteMediaUrl, normalizeTheaterFolderKey } from "../utils/theaterGalleryImages";
import "../adminConsoleTheme.css";

function uploadErrorMessage(err) {
  const d = err?.response?.data;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && d.message != null) return String(d.message);
  return err?.message || "Request failed.";
}

export default function AdminTheatreGallery() {
  const navigate = useNavigate();
  const { context } = useContext(Context);

  const [theaters, setTheaters] = useState([]);
  const [selectedName, setSelectedName] = useState("");
  const [sectionTab, setSectionTab] = useState("details");
  const [images, setImages] = useState([]);
  const [legacyFiles, setLegacyFiles] = useState([]);
  const [legacyConfigured, setLegacyConfigured] = useState(false);
  const [loadingLegacy, setLoadingLegacy] = useState(false);
  const [loadingTheaters, setLoadingTheaters] = useState(true);
  const [loadingImages, setLoadingImages] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [banner, setBanner] = useState(null);
  const [file, setFile] = useState(null);
  const [videos, setVideos] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [loadingVideos, setLoadingVideos] = useState(false);

  const theaterKey = selectedName ? normalizeTheaterFolderKey(selectedName) : "";

  useEffect(() => {
    if (!context.isAdminLoggedIn) {
      navigate("/admin", { replace: true });
      return;
    }
    if (String(context.adminRole || "").toUpperCase() !== "SUPER_ADMIN") {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [context.isAdminLoggedIn, context.adminRole, navigate]);

  const showBanner = useCallback((variant, message) => {
    setBanner({ variant, message });
    window.setTimeout(() => setBanner(null), 6000);
  }, []);

  useEffect(() => {
    axios
      .get(ADMIN_THEATRE_GALLERY_SETTINGS)
      .then((res) => {
        setLegacyConfigured(Boolean(res.data?.legacySourceConfigured));
      })
      .catch(() => setLegacyConfigured(false));
  }, []);

  const loadTheaters = useCallback(() => {
    setLoadingTheaters(true);
    axios
      .get(ADMIN_THEATRE_GALLERY_THEATERS)
      .then((res) => {
        setTheaters(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error(err);
        showBanner("danger", "Could not load theatre list from API.");
        setTheaters([]);
      })
      .finally(() => setLoadingTheaters(false));
  }, [showBanner]);

  useEffect(() => {
    loadTheaters();
  }, [loadTheaters]);

  const loadImages = useCallback(() => {
    if (!theaterKey) {
      setImages([]);
      return;
    }
    setLoadingImages(true);
    axios
      .get(ADMIN_THEATRE_GALLERY_IMAGES, {
        params: { theaterKey, activeOnly: false },
      })
      .then((res) => {
        setImages(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error(err);
        showBanner("danger", "Could not load images for this theatre.");
        setImages([]);
      })
      .finally(() => setLoadingImages(false));
  }, [theaterKey, showBanner]);

  const loadVideos = useCallback(() => {
    if (!theaterKey) {
      setVideos([]);
      return;
    }
    setLoadingVideos(true);
    axios
      .get(ADMIN_THEATRE_GALLERY_VIDEOS, { params: { theaterKey } })
      .then((res) => {
        setVideos(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error(err);
        showBanner("danger", "Could not load videos for this theatre.");
        setVideos([]);
      })
      .finally(() => setLoadingVideos(false));
  }, [theaterKey, showBanner]);

  const loadLegacyFiles = useCallback(() => {
    if (!theaterKey || !legacyConfigured) {
      setLegacyFiles([]);
      return;
    }
    setLoadingLegacy(true);
    axios
      .get(ADMIN_THEATRE_GALLERY_LEGACY, { params: { theaterKey } })
      .then((res) => {
        setLegacyFiles(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setLegacyFiles([]))
      .finally(() => setLoadingLegacy(false));
  }, [theaterKey, legacyConfigured]);

  useEffect(() => {
    loadLegacyFiles();
  }, [loadLegacyFiles]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  const onUpload = (e) => {
    e.preventDefault();
    if (!theaterKey || !file) {
      showBanner("warning", "Pick a theatre and choose a file.");
      return;
    }
    const fd = new FormData();
    fd.append("theaterKey", theaterKey);
    fd.append("file", file);
    setUploading(true);
    axios
      .post(ADMIN_THEATRE_GALLERY_UPLOAD, fd)
      .then(() => {
        setFile(null);
        showBanner("success", "Image uploaded.");
        loadImages();
      })
      .catch((err) => {
        showBanner("danger", uploadErrorMessage(err) || "Upload failed.");
      })
      .finally(() => setUploading(false));
  };

  const importLegacy = (fileName) => {
    axios
      .post(ADMIN_THEATRE_GALLERY_IMPORT_LEGACY, { theaterKey, fileName })
      .then(() => {
        showBanner("success", "Imported into managed gallery.");
        loadImages();
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || err?.message || "Import failed.";
        showBanner("danger", msg);
      });
  };

  const toggleActive = (id, active) => {
    axios
      .post(adminTheatreGallerySetActive(id), { active }, {
        headers: { "Content-Type": "application/json" },
      })
      .then(() => {
        showBanner("success", active ? "Image activated." : "Image hidden from the public gallery.");
        loadImages();
      })
      .catch((err) => {
        console.error(err);
        showBanner("danger", "Update failed.");
      });
  };

  const deleteImageRow = (id) => {
    if (!window.confirm("Delete this image from disk and the database? This cannot be undone.")) {
      return;
    }
    axios
      .delete(adminTheatreGalleryDeleteImage(id))
      .then(() => {
        showBanner("success", "Image deleted.");
        loadImages();
      })
      .catch((err) => {
        showBanner("danger", uploadErrorMessage(err) || "Delete failed.");
      });
  };

  const onUploadVideo = (e) => {
    e.preventDefault();
    if (!theaterKey || !videoFile) {
      showBanner("warning", "Pick a theatre and choose a video file (mp4, webm, or mov).");
      return;
    }
    const fd = new FormData();
    fd.append("theaterKey", theaterKey);
    fd.append("file", videoFile);
    setUploadingVideo(true);
    axios
      .post(ADMIN_THEATRE_GALLERY_UPLOAD_VIDEO, fd)
      .then(() => {
        setVideoFile(null);
        showBanner("success", "Video uploaded. Toggle “Active” to control which clips appear on Book Now.");
        loadVideos();
      })
      .catch((err) => {
        showBanner("danger", uploadErrorMessage(err) || "Video upload failed.");
      })
      .finally(() => setUploadingVideo(false));
  };

  const toggleVideoActiveRow = (id, active) => {
    axios
      .post(adminTheatreGallerySetVideoActive(id), { active }, {
        headers: { "Content-Type": "application/json" },
      })
      .then(() => {
        showBanner("success", active ? "Video is now active on Book Now." : "Video hidden from Book Now.");
        loadVideos();
      })
      .catch((err) => {
        console.error(err);
        showBanner("danger", err?.response?.data?.message || "Update failed.");
      });
  };

  const deleteVideoRow = (id) => {
    if (!window.confirm("Delete this video from disk and the database? This cannot be undone.")) {
      return;
    }
    axios
      .delete(adminTheatreGalleryDeleteVideo(id))
      .then(() => {
        showBanner("success", "Video deleted.");
        loadVideos();
      })
      .catch((err) => {
        showBanner("danger", uploadErrorMessage(err) || "Delete failed.");
      });
  };

  return (
    <AdminLayout title="Update theatre details">
      <div className="admin-gallery-scope">
        {banner ? (
          <Alert variant={banner.variant} className="mb-3 border-0" style={{ opacity: 0.95 }}>
            {banner.message}
          </Alert>
        ) : null}

        <div className="admin-gallery-panel">
          <div className="row g-3 align-items-end mb-3 pb-3 border-bottom border-secondary border-opacity-25">
            <div className="col-md-8 col-lg-6">
              <Form.Label>Theatre</Form.Label>
              {loadingTheaters ? (
                <Spinner animation="border" size="sm" className="ms-2" />
              ) : (
                <Form.Select
                  value={selectedName}
                  onChange={(e) => setSelectedName(e.target.value)}
                >
                  <option value="">— Select theatre —</option>
                  {theaters.map((t) => (
                    <option key={t.id} value={t.name || ""}>
                      {t.name}
                    </option>
                  ))}
                </Form.Select>
              )}
            </div>
            {theaterKey ? (
              <div className="col-md-4 col-lg-6 text-md-end">
                <small className="text-secondary">
                  Storage key: <strong className="text-light">{theaterKey}</strong>
                </small>
              </div>
            ) : null}
          </div>

          <Nav
            variant="tabs"
            className="admin-gallery-tabs mb-3"
            activeKey={sectionTab}
            onSelect={(k) => k && setSectionTab(k)}
          >
            <Nav.Item>
              <Nav.Link eventKey="details">Theatre &amp; slots</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="images">Images</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="videos">Videos</Nav.Link>
            </Nav.Item>
          </Nav>

          {sectionTab === "details" ? (
            <AdminTheatreDetailsTable showBanner={showBanner} />
          ) : !theaterKey ? (
            <p className="text-secondary small mb-0">
              Select a theatre above to manage {sectionTab === "images" ? "images" : "videos"}.
            </p>
          ) : sectionTab === "images" ? (
            <>
              <Form onSubmit={onUpload} className="mb-4 pb-3 border-bottom border-secondary border-opacity-25">
                <h6 className="mb-3">Upload image</h6>
                <div className="row g-2 align-items-end">
                  <div className="col-md-8">
                    <Form.Label>File (jpg, png, webp, gif)</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.gif"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                      disabled={uploading}
                    />
                  </div>
                  <div className="col-md-4">
                    <Button type="submit" variant="primary" disabled={!file || uploading}>
                      {uploading ? "Uploading…" : "Upload"}
                    </Button>
                  </div>
                </div>
              </Form>

              {loadingImages ? (
                <Spinner animation="border" size="sm" />
              ) : images.length === 0 ? (
                <p className="text-secondary small mb-0">
                  No managed images for this key. Upload above or import from legacy (if enabled).
                </p>
              ) : (
                <div className="row g-3">
                  {images.map((img) => (
                    <div key={img.id} className="col-6 col-md-4 col-lg-3">
                      <div
                        className={`admin-gallery-media-card h-100 ${img.active ? "" : "inactive"}`}
                      >
                        <div className="ratio ratio-4x3 bg-black">
                          <img
                            src={absoluteMediaUrl(img.url)}
                            alt={img.originalFileName || ""}
                            style={{ objectFit: "cover", width: "100%", height: "100%" }}
                          />
                        </div>
                        <div className="caption">
                          <div className="text-truncate text-light" title={img.originalFileName}>
                            {img.originalFileName}
                          </div>
                          <div className="d-flex flex-wrap gap-1 mt-2">
                            <Button
                              size="sm"
                              variant="outline-light"
                              type="button"
                              href={adminTheatreGalleryDownloadImage(img.id)}
                              as="a"
                            >
                              Download
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              type="button"
                              onClick={() => deleteImageRow(img.id)}
                            >
                              Delete
                            </Button>
                            {img.active ? (
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                type="button"
                                onClick={() => toggleActive(img.id, false)}
                              >
                                Deactivate
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="success"
                                type="button"
                                onClick={() => toggleActive(img.id, true)}
                              >
                                Activate
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {legacyConfigured && theaterKey ? (
                <>
                  <h6 className="mt-5 mb-3">Legacy folder (read-only)</h6>
                  {loadingLegacy ? (
                    <Spinner animation="border" size="sm" />
                  ) : legacyFiles.length === 0 ? (
                    <p className="text-secondary small">
                      No files in <code>legacy-source-dir/{theaterKey}/</code>.
                    </p>
                  ) : (
                    <div className="row g-3">
                      {legacyFiles.map((f) => (
                        <div key={f.fileName} className="col-6 col-md-4 col-lg-3">
                          <div className="admin-gallery-media-card">
                            <div className="ratio ratio-4x3 bg-black">
                              <img
                                src={absoluteMediaUrl(f.url)}
                                alt={f.fileName}
                                style={{ objectFit: "cover", width: "100%", height: "100%" }}
                              />
                            </div>
                            <div className="caption">
                              <div className="text-truncate" title={f.fileName}>
                                {f.fileName}
                              </div>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="mt-1"
                                type="button"
                                onClick={() => importLegacy(f.fileName)}
                              >
                                Import
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : null}
            </>
          ) : (
            <>
              <Form
                onSubmit={onUploadVideo}
                className="mb-4 pb-3 border-bottom border-secondary border-opacity-25"
              >
                <h6 className="mb-3">Upload video</h6>
                <div className="row g-2 align-items-end">
                  <div className="col-md-8">
                    <Form.Label>
                      File <span className="text-danger">*</span> (mp4, webm, mov)
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept=".mp4,.webm,.mov,video/mp4,video/webm,video/quicktime"
                      onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                      disabled={uploadingVideo}
                      required
                    />
                    <Form.Text className="text-secondary">
                      Any number of files can be active; order follows upload sequence.
                    </Form.Text>
                  </div>
                  <div className="col-md-4">
                    <Button type="submit" variant="primary" disabled={!videoFile || uploadingVideo}>
                      {uploadingVideo ? "Uploading…" : "Upload video"}
                    </Button>
                  </div>
                </div>
              </Form>

              {loadingVideos ? (
                <Spinner animation="border" size="sm" />
              ) : videos.length === 0 ? (
                <p className="text-secondary small mb-0">No uploaded videos for this theatre.</p>
              ) : (
                <div className="row g-3">
                  {videos.map((v) => (
                    <div key={v.id} className="col-12 col-md-6">
                      <div className={`admin-gallery-media-card ${v.active ? "" : "inactive"}`}>
                        <video
                          src={absoluteMediaUrl(v.url)}
                          controls
                          muted
                          playsInline
                          className="w-100 bg-black"
                          style={{ maxHeight: 240, objectFit: "contain" }}
                        />
                        <div className="caption">
                          <div className="text-truncate text-light" title={v.originalFileName}>
                            {v.originalFileName}
                          </div>
                          <div className="mt-1 mb-2">
                            {v.active ? (
                              <span className="badge bg-success">Active</span>
                            ) : (
                              <span className="badge bg-secondary">Inactive</span>
                            )}
                          </div>
                          <div className="d-flex flex-wrap gap-1">
                            <Button
                              size="sm"
                              variant="outline-light"
                              as="a"
                              href={adminTheatreGalleryDownloadVideo(v.id)}
                            >
                              Download
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              type="button"
                              onClick={() => deleteVideoRow(v.id)}
                            >
                              Delete
                            </Button>
                            {v.active ? (
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                type="button"
                                onClick={() => toggleVideoActiveRow(v.id, false)}
                              >
                                Deactivate
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="success"
                                type="button"
                                onClick={() => toggleVideoActiveRow(v.id, true)}
                              >
                                Activate
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
